/**
 * InsightCard Component
 *
 * Base wrapper component for all insight card variants.
 * Provides consistent styling and click behavior.
 */

import React from 'react';
import { Box, Paper, Typography, Skeleton } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';

interface InsightCardProps {
  /** Label shown above the card content (e.g., "Your Top Pick") */
  label: string;
  /** Brief rationale for this insight */
  rationale: string;
  /** Route to navigate to when clicked */
  deepLinkRoute: string;
  /** Click handler for navigation */
  onNavigate: (route: string) => void;
  /** Whether data is still loading */
  isLoading?: boolean;
  /** Whether to show data source attribution (default: true) */
  showDataSource?: boolean;
  /** Card content */
  children: React.ReactNode;
}

const InsightCard: React.FC<InsightCardProps> = ({
  label,
  rationale,
  deepLinkRoute,
  onNavigate,
  isLoading = false,
  showDataSource = true,
  children,
}) => {
  const handleClick = () => {
    onNavigate(deepLinkRoute);
  };

  return (
    <Paper
      elevation={0}
      onClick={handleClick}
      sx={{
        p: 1.5,
        cursor: 'pointer',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        transition: 'all 0.2s ease',
        position: 'relative',
        '&:hover': {
          borderColor: 'text.primary',
          transform: 'translateX(4px)',
        },
      }}
    >
      {/* Label + Rationale header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              fontSize: 10,
              letterSpacing: 1.2,
              lineHeight: 1.2,
              display: 'block',
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
              fontSize: 10,
              display: 'block',
              mt: 0.25,
            }}
          >
            {rationale}
          </Typography>
        </Box>

        {/* Arrow icon (visible on hover) */}
        <ArrowForward
          sx={{
            fontSize: 16,
            color: 'text.secondary',
            opacity: 0,
            transition: 'opacity 0.2s',
            '.MuiPaper-root:hover &': { opacity: 1 },
          }}
        />
      </Box>

      {/* Content area */}
      {isLoading ? (
        <Box>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="40%" height={20} />
          <Skeleton variant="text" width="80%" height={16} sx={{ mt: 0.5 }} />
        </Box>
      ) : (
        children
      )}

      {/* Data source attribution */}
      {showDataSource && !isLoading && (
        <Box
          sx={{
            pt: 1,
            mt: 1,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: 'text.disabled', fontSize: 9 }}
          >
            Live Data
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default InsightCard;
